import { useEffect, useState } from 'react';
import { Expand } from 'lucide-react';
import { cn } from '@/lib/cn';
import { PhotoViewer } from './PhotoViewer';

/*
  A person's photo, with the initial as the fallback.

  Rendering `<img>` directly means a URL that 404s leaves the browser's broken-
  image glyph on the screen — which is what happened when photo URLs pointed at
  an S3 bucket that was not there. A broken icon is worse than no photo: on the
  gate screen an officer is comparing a face against it, and a grey square with a
  torn-page icon reads as "the photo has not loaded yet", not "there is no photo".

  So a load failure falls back to exactly the same square the no-photo case uses.
  `key`ing the state to the src resets it when the photo changes — otherwise an
  avatar that failed once stays failed after a successful re-upload.

  ── Openable ────────────────────────────────────────────────────────────────

  Every photo here is drawn at between 36 and 80 pixels, which is enough to
  recognise somebody you already know and not enough to decide whether the face
  in front of you is the same one. `zoomable` makes the photo a button that
  opens it properly — big, rotatable, movable. See PhotoViewer.

  It is a prop rather than the default because not every avatar is evidence: the
  initial-only fallback has nothing to show, and an avatar used purely as
  decoration beside a name should not advertise a click that reveals a blurry
  enlargement of nothing.
*/
export function Avatar({
  src,
  name,
  size = 40,
  className,
  zoomable = false,
  caption,
}: {
  src?: string | null;
  name: string;
  /** Rendered square, in px. */
  size?: number;
  className?: string;
  /** Click to open the photo full-size, rotatable and pannable. */
  zoomable?: boolean;
  /** A second line in the viewer — a class, or a relationship. */
  caption?: string;
}) {
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const box = cn(
    'flex-shrink-0 rounded-md border border-border object-cover',
    className,
  );
  const style = { width: size, height: size };

  if (!src || failed) {
    return (
      <span
        aria-hidden
        style={{ ...style, fontSize: Math.max(11, Math.round(size * 0.34)) }}
        className={cn(box, 'flex items-center justify-center bg-muted font-bold text-muted-foreground')}
      >
        {name?.[0]?.toUpperCase() ?? '?'}
      </span>
    );
  }

  const image = (
    <img
      src={src}
      alt={name}
      style={style}
      className={box}
      onError={() => setFailed(true)}
      /* The photo is decoration beside the name, which is already on screen —
         it must never hold up the rest of the card. */
      loading="lazy"
      decoding="async"
    />
  );

  if (!zoomable) return image;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          /* Rows are clickable on several of these screens; opening a photo
             must not also open the row behind it. */
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={`See ${name}'s photo larger`}
        title={`See ${name}'s photo larger`}
        style={style}
        className="group relative flex-shrink-0 overflow-clip rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {image}
        {/*
          The affordance. Without it a photo that can be opened looks exactly
          like one that cannot, and nobody at a gate goes round tapping things
          to find out.
        */}
        <span className="absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <Expand size={Math.max(11, Math.round(size * 0.3))} className="text-white" />
        </span>
      </button>

      {open && (
        <PhotoViewer src={src} alt={name} caption={caption} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
